<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->

<template>
  <div>
    <h2>Use this form to request a registry<br>&nbsp;</h2>
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-8">
          <div class="card shadow p-3">
            <form id="irrForm" method="POST" enctype="multipart/form-data" action="/irr-request/">
              <input type="hidden" name="_token" :value="csrf_token">
              <ul class="alert alert-danger errorMessages" role="alert"></ul>
              <p>
                <span class="font-weight-bold">Registries control the publication of IoT resource descriptions.</span> Use this form to request that we create a registry that you or your organization can manage. Typically a registry will correspond to a <span class="font-weight-bold">collection of related IoT resources</span>, such as IoT resources in a mall, in a city neighborhood, in an office building, or some other venue.
              </p>
              <p>
                <span class="font-weight-bold">By requesting that we create a registry for you or your organization to manage, you agree to take responsibility for the content you publish in the registry, as detailed in our <a href="/terms-of-use">terms of use</a>.</span> You have the ability to accept or reject requests submitted by contributors to have their resource descriptions published in your registry, and also have the ability to later unpublish these descriptions if you determine they are problematic.
              </p>
              <p>
                To submit a request for a registry, you have to provide us with some information, which will be used to vet your request. This information has to be complete and accurate, as also detailed in our <a href="/terms-of-use">terms of use</a>. <span class="font-weight-bold">We will validate your request and contact you at the email address you provide.</span> This may include requests for further clarification, notification of our decision, and, if approved, next steps for managing your or your organization’s registry.
              </p>
              <p>Fields marked with an asterisk <span class="font-weight-bold">*</span> are required<br>&nbsp;</p>

              <hr>
              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="irr_owner_name">* Registry Owner</label>
                  <small id="irr_owner_name-help" class="form-text text-muted">Please enter the legal name of the individual or organization responsible for this registry.</small>
                </h5>
                <input type="text" class="form-control" id="irr_owner_name" name="irr_owner_name" aria-describedby="irr_owner_name-help" v-model="formData.irr_owner_name" placeholder="" maxlength="75" required="">
                <br />
                <h6 style="color: black !important; margin-top: 5px;">* Please indicate whether this registry is created by an established organization or an individual/informal group:</h6>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="org_legal" value="1" id="org_legal_true" required="">
                  <label class="form-check-label" for="org_legal_true">An established organization (e.g., a business, non-profit, or government entity)</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="org_legal" value="0" id="org_legal_false" required="">
                  <label class="form-check-label" for="org_legal_false">An individual or informal group (e.g., a neighborhood association, community project, or personal initiative)</label>
                </div>
                <div class="form-check mt-2">
                  <input class="form-check-input" type="checkbox" id="irr_representation_acknowledgement" name="irr_representation_acknowledgement" value="1" required>
                  <label class="form-check-label font-weight-bold" for="irr_representation_acknowledgement">* I am authorized to represent the organization named above OR I am acting as an individual and take personal responsibility for the content published in this registry.</label>
                </div>
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="org_type">Type of Organization <span class="font-weight-normal">(if applicable)</span></label>
                  <small id="org_type-help" class="form-text text-muted">e.g. transportation, government, retail, education, neighborhood organization, association, finance, health, utilities, services, manufacturing, engineering, construction, farming, IT</small>
                </h5>
                <input type="text" class="form-control" id="org_type" name="org_type" v-model="formData.org_type" placeholder="" maxlength="50">
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="irr_owner_address">* Address</label>
                  <small id="irr_owner_address-help" class="form-text text-muted">The address associated with the registry owner.</small>
                </h5>
                <div class="form-group row">
                  <label for="irr_owner_address_street" class="col-sm-2 col-form-label text-right">* Street</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="irr_owner_address_street" name="irr_owner_address_street" v-model="formData.irr_owner_address_street" placeholder="" maxlength="50" required="">
                  </div>
                </div>
                <div class="form-group row">
                  <label for="irr_owner_address_city" class="col-sm-2 col-form-label text-right">* City</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="irr_owner_address_city" name="irr_owner_address_city" v-model="formData.irr_owner_address_city" placeholder="" maxlength="50" required="">
                  </div>
                </div>
                <div class="form-group row">
                  <label for="irr_owner_address_state" class="col-sm-2 col-form-label text-right">* State</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="irr_owner_address_state" name="irr_owner_address_state" v-model="formData.irr_owner_address_state" placeholder="" maxlength="50" required="">
                  </div>
                </div>
                <div class="form-group row">
                  <label for="irr_owner_address_zip" class="col-sm-2 col-form-label text-right">* Zip</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="irr_owner_address_zip" name="irr_owner_address_zip" v-model="formData.irr_owner_address_zip" placeholder="" maxlength="15" required="">
                  </div>
                </div>
                <div class="form-group row">
                  <label for="irr_owner_address_country" class="col-sm-2 col-form-label text-right">* Country</label>
                  <div class="col-sm-10">
                    <select class="form-control" id="irr_owner_address_country" name="irr_owner_address_country" v-model="formData.irr_owner_address_country" required="">
                      <option>United States</option>
                      <option>Canada</option>
                      <option>Australia</option>
                      <option>New Zealand</option>
                      <option>Iceland</option>
                      <option>Liechtenstein</option>
                      <option>Norway</option>
                      <option>Switzerland</option>
                      <option>United Kingdom</option>
                      <option>Member of the European Union</option>
                    </select>
                  </div>
                </div>
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="contact">* Contact</label>
                  <small id="contact-help" class="form-text text-muted">Provide information we can use to contact you about this registry request</small>
                </h5>
                <div class="form-group row">
                  <label for="contact_name" class="col-sm-2 col-form-label text-right">* Name</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="contact_name" name="contact_name" v-model="formData.contact_name" placeholder="" maxlength="75" required="">
                    <small id="contact_name-help" class="form-text text-muted">Legal name of individual</small>
                  </div>
                </div>
                <div class="form-group row">
                  <label for="contact_email" class="col-sm-2 col-form-label text-right">* Email</label>
                  <div class="col-sm-10">
                    <input type="email" class="form-control" id="contact_email" name="contact_email" v-model="formData.contact_email" placeholder="" required="">
                    <small id="contact_email-help" class="form-text text-muted">Valid email address (please use your organization email address if applicable)</small>
                  </div>
                </div>
                <div class="form-group row">
                  <label for="contact_title" class="col-sm-2 col-form-label text-right">Title/Role</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="contact_title" name="contact_title" v-model="formData.contact_title" placeholder="" maxlength="50">
                    <small id="contact_title-help" class="form-text text-muted">If an organization is responsible for this registry please provide your Title/Role</small>
                  </div>
                </div>
                <div class="form-group row" style="display: none;">
                  <label for="contact_role" class="col-sm-2 col-form-label text-right">Role</label>
                  <div class="col-sm-10">
                    <input type="text" class="form-control" id="contact_role" name="contact_role" v-model="formData.contact_role" placeholder="" maxlength="50">
                    <small id="contact_role-help" class="form-text text-muted">If an organization is responsible for this registry please provide your Role</small>
                  </div>
                </div>
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="proposed_irr_name">* Proposed Name for your Registry</label>
                  <small id="proposed_irr_name-help" class="form-text text-muted">This should be a self-explanatory name.  Note that misappropriation or use of someone else’s name or trademark is a serious offense.</small>
                </h5>
                <input type="text" class="form-control" id="proposed_irr_name" name="proposed_irr_name" v-model="formData.proposed_irr_name" placeholder="" maxlength="30" required="">
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="publisher_name">* Publisher Name</label>
                  <small id="publisher_name-help" class="form-text text-muted">The Publisher Name is the name that will be shown with all IoT resources published in this registry. Note that misappropriation of of someone else's name or trademark is a serios offense.</small>
                </h5>
                <input type="text" class="form-control" id="publisher_name" name="publisher_name" v-model="formData.publisher_name" placeholder="" maxlength="30" required="">
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="irr_description">* Brief Description of the Registry</label>
                  <small id="irr_description-help" class="form-text text-muted">Describe the purpose and expected usage of the registry you are requesting, such as the types of resources you expect to see being published in the registry and who you expect to submit requests for publication.</small>
                </h5>
                <textarea class="form-control" id="irr_description" name="irr_description" rows="3" aria-describedby="irr_description-help" maxlength="240" v-model="formData.irr_description" required=""></textarea>
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="country">* Location</label>
                  <small id="country-help" class="form-text text-muted">Indicate where the requested registry will be located. Please note that we currently only support registries in the United States of America, Canada, Australia, New Zealand, Iceland, Liechtenstein, Norway, Switzerland, the United Kingdom, or a member state of the European Union.</small>
                </h5>
                <select class="form-control" id="country" name="country" v-model="formData.country" required="">
                  <option>United States</option>
                  <option>Canada</option>
                  <option>Australia</option>
                  <option>New Zealand</option>
                  <option>Iceland</option>
                  <option>Liechtenstein</option>
                  <option>Norway</option>
                  <option>Switzerland</option>
                  <option>United Kingdom</option>
                  <option>Member of the European Union</option>
                </select>
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <input type="hidden" id="latitude" name="latitude" v-model="formData.latitude">
                <input type="hidden" id="longitude" name="longitude" v-model="formData.longitude">
                <div id="irrmap" style="height: 400px"></div>
                <div class="mt-3">
                  <label for="map_address_search" class="font-weight-bold"></label>
                  <small class="form-text text-muted">Enter an address and click Find Address to move and zoom the map near that location.</small>
                  <div class="map-address-search-row">
                    <input type="text" class="form-control" id="map_address_search" v-model.trim="mapAddressSearch" @input="handleAddressInput" @keyup.enter.prevent="handleAddressSearch" placeholder="e.g. 5000 Forbes Avenue, Pittsburgh PA">
                    <button class="btn btn-primary mt-0 map-find-address-button" type="button" @click.stop.prevent="handleAddressSearch" :disabled="mapSearchLoading">{{ mapSearchLoading ? 'Searching...' : 'Find Address' }}</button>
                  </div>
                  <ul v-if="mapAddressSuggestions.length" class="list-group map-address-suggestions">
                      <li v-for="(suggestion, index) in mapAddressSuggestions" :key="index" class="list-group-item list-group-item-action" @mousedown.stop.prevent="selectAddressSuggestion(suggestion)">{{ suggestion.label }}</li>
                  </ul>
                  <small v-if="mapSearchError" class="form-text text-danger">{{ mapSearchError }}</small>
                </div>
                <div class="alert alert-primary map-location-help-banner" role="alert"><center><h5>Click anywhere on the map to set your Registry's location.  Adjust its area of coverage using the Radius setting below.</h5></center></div>
              </div>
              <p>&nbsp;</p>

              <div class="form-group">
                <h5 class="font-weight-bold">
                  <label for="radius"></label>
                  <small id="radius-help" class="form-text text-muted">* Enter the radius <em>in meters</em> that your registry spans. Max 2000 meters.</small>
                </h5>
                <input type="number" name="radius" class="form-control" id="radius" v-model="formData.radius" min="0" :max="maxRadius" placeholder="" required="">
              </div>
              <p>&nbsp;</p>

              <a href="#" onclick="event.preventDefault(); window.history.back();" class="btn btn-danger">Cancel</a>
              <button type="submit" class="btn btn-success">Request Registry</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        route: String,
        csrf_token: String,
        errors: Array,
        existingData: [Object, Array],
        maxRadius: Number,
      },
      data: function () {
        return {
          map: false,
          mapMarker: false,
          defaultMapZoom: 15,
          formData: {
            irr_owner_name: (this.existingData && this.existingData.irr_owner_name) ? this.existingData.irr_owner_name : '',
            org_type: (this.existingData && this.existingData.org_type) ? this.existingData.org_type : '',
            irr_owner_address_street: (this.existingData && this.existingData.irr_owner_address_street) ? this.existingData.irr_owner_address_street : '',
            irr_owner_address_city: (this.existingData && this.existingData.irr_owner_address_city) ? this.existingData.irr_owner_address_city : '',
            irr_owner_address_state: (this.existingData && this.existingData.irr_owner_address_state) ? this.existingData.irr_owner_address_state : '',
            irr_owner_address_zip: (this.existingData && this.existingData.irr_owner_address_zip) ? this.existingData.irr_owner_address_zip : '',
            irr_owner_address_country: (this.existingData && this.existingData.irr_owner_address_country) ? this.existingData.irr_owner_address_country : 'United States',
            contact_name: (this.existingData && this.existingData.contact_name) ? this.existingData.contact_name : '',
            contact_email: (this.existingData && this.existingData.contact_email) ? this.existingData.contact_email : '',
            contact_title: (this.existingData && this.existingData.contact_title) ? this.existingData.contact_title : '',
            contact_role: (this.existingData && this.existingData.contact_role) ? this.existingData.contact_role : '',
            proposed_irr_name: (this.existingData && this.existingData.proposed_irr_name) ? this.existingData.proposed_irr_name : '',
            publisher_name: (this.existingData && this.existingData.publisher_name) ? this.existingData.publisher_name : '',
            irr_description: (this.existingData && this.existingData.irr_description) ? this.existingData.irr_description : '',
            country: (this.existingData && this.existingData.country) ? this.existingData.country : 'United States',
            latitude: (this.existingData && this.existingData.latitude) ? this.existingData.latitude : 500,
            longitude: (this.existingData && this.existingData.longitude) ? this.existingData.longitude : 500,
            radius: (this.existingData && this.existingData.radius) ? this.existingData.radius : '',
          },
          mapAddressSearch: '',
          mapAddressSuggestions: [],
          mapSearchError: '',
          mapSearchLoading: false,
          mapSearchDebounceTimer: false,
        }
      },
      beforeDestroy() {
        if(this.mapSearchDebounceTimer) {
          clearTimeout(this.mapSearchDebounceTimer);
        }
      },
      mounted() {
        this.$nextTick(function () {  //don't run until the entire view has been rendered
          $('form').each(this.createAllErrors);
          this.initMap();

          //display validation errors sent from server
          this.showServerErrors();

          //pre-select org_legal button when applicable
          if(this.existingData.hasOwnProperty('org_legal')) {
            if(this.existingData.org_legal == '1') {
              $('#org_legal_true').prop("checked", true);
            } else {
              $('#org_legal_false').prop("checked", true);
            }
          }
        })
      },
      methods: {
        handleAddressInput: function() {
          this.mapSearchError = '';

          if(this.mapSearchDebounceTimer) {
            clearTimeout(this.mapSearchDebounceTimer);
          }

          if(!this.mapAddressSearch || this.mapAddressSearch.length < 3) {
            this.mapAddressSuggestions = [];
            return;
          }

          this.mapSearchDebounceTimer = setTimeout(async () => {
            var suggestions = await this.fetchAddressSuggestions(this.mapAddressSearch, 5);
            this.mapAddressSuggestions = suggestions;
          }, 300);
        },
        fetchAddressSuggestions: async function(query, limit) {
          if(!query) {
            return [];
          }

          try {
            var encodedAddress = encodeURIComponent(query);
            var response = await fetch('https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=' + limit + '&q=' + encodedAddress, {
              headers: {
                'Accept': 'application/json'
              }
            });

            if(!response.ok) {
              return [];
            }

            var geocodeResults = await response.json();
            if(!Array.isArray(geocodeResults)) {
              return [];
            }

            return geocodeResults.map(function(result) {
              return {
                label: result.display_name,
                lat: parseFloat(result.lat),
                lng: parseFloat(result.lon),
              };
            }).filter(function(result) {
              return !isNaN(result.lat) && !isNaN(result.lng);
            });
          } catch (error) {
            return [];
          }
        },
        selectAddressSuggestion: function(suggestion) {
          this.mapAddressSearch = suggestion.label;
          this.mapAddressSuggestions = [];
          this.moveMapToCoordinates(suggestion.lat, suggestion.lng);
        },
        moveMapToCoordinates: function(lat, lng) {
          this.$data.map.setView([lat, lng], Math.max(this.$data.defaultMapZoom, 16));
          this.handleMapClick({latlng: {lat: lat, lng: lng}});
        },
        handleAddressSearch: async function() {
          this.mapSearchError = '';
          this.mapAddressSuggestions = [];

          if(!this.mapAddressSearch) {
            this.mapSearchError = 'Please enter an address to search.';
            return;
          }

          this.mapSearchLoading = true;

          try {
            var geocodeResults = await this.fetchAddressSuggestions(this.mapAddressSearch, 5);

            if(!Array.isArray(geocodeResults) || !geocodeResults.length) {
              this.mapSearchError = 'Address not found. Please try a more specific address.';
              return;
            }

            this.mapAddressSuggestions = geocodeResults;
            var topResult = geocodeResults[0];

            this.moveMapToCoordinates(topResult.lat, topResult.lng);
          } catch (error) {
            this.mapSearchError = 'Unable to search for that address right now. Please try again.';
          } finally {
            this.mapSearchLoading = false;
          }
        },
        initMap: function() {
          //init the map
          this.$data.map = window.L.map('irrmap').setView([39.8, -98.6], 4); //default to United States view

          //init some map components
          this.$data.mapMarker = L.marker([39.8, -98.6]);
      		this.$data.mapCircle = L.circle([39.8, -98.6], {
      			color: 'orange',
      			fillColor: '#ffa200',
      			fillOpacity: 0.35,
            radius: this.formData.radius
      		});

          //add the content
          //L.tileLayer('/map/tile/{s}/{id}/{z}/{x}/{y}', {  //for use when proxying the tile requests

          /* DEPRECATED L.tileLayer('https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}', {
      			attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/" target="_blank">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/" target="_blank">CC-BY-SA</a>',
      			maxZoom: 18,
            minZoom: 3,
      			id: 'mapbox.streets',
      			accessToken: 'pk.eyJ1IjoiamRvbm5lbGwiLCJhIjoiY2ptZHN1azhqMHR2MDNxbnF2eWloOTRoeSJ9.RlFLdSY8e_anTpJNUJhIkA'
      		}).addTo(this.$data.map); */

          L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
            attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
            tileSize: 512,
            maxZoom: 18,
            minZoom: 3,
            zoomOffset: -1,
            id: 'jdonnell/ckg9vp1o403lx1as8i81og3ir',
            accessToken: 'pk.eyJ1IjoiamRvbm5lbGwiLCJhIjoiY2ptZHN1azhqMHR2MDNxbnF2eWloOTRoeSJ9.RlFLdSY8e_anTpJNUJhIkA'
          }).addTo(this.$data.map);

          window.L.control.scale().addTo(this.map);

          /* check if existing data has valid LatLng set */
          var validLatLng = true;
          //latitude check
          if('latitude' in this.existingData) {  //check that value exists
            if(!this.existingData.latitude || !(this.existingData.latitude <= 90 && this.existingData.latitude >= -90)) {  //check valid range
              validLatLng = false;
            }
          } else { validLatLng = false; }
          //longitude check
          if('longitude' in this.existingData) {  //check that value exists
            if(!this.existingData.longitude || !(this.existingData.longitude <= 180 && this.existingData.longitude >= -180)) {  //check valid range
              validLatLng = false;
            }
          } else { validLatLng = false; }

          //if valid LatLng, move the map to that location and place a marker
          if(validLatLng) {
            this.$data.map.setView([this.existingData.latitude, this.existingData.longitude], this.$data.defaultMapZoom);
            var fakeEvent = {latlng: {lat: this.existingData.latitude, lng: this.existingData.longitude}};
            this.handleMapClick(fakeEvent);
          }
          //else attempt to get LatLng based on ip address, then move map to that location
          else {
            //attempt to get LatLng based on ip address, then move map to that location
            var self = this;
            //geolocation via browser
            if(navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(self.setMapLatLng);
            } else { //Geolocation is not supported by this browser
              this.$data.mapPopup
                .setLatLng([$('#latitude').val(), $('#longitude').val()])
                .setContent('<p>Geolocation is not supported by this browser</p>')
                .openOn(this.$data.map);
            }
          }

          //handle map clicks
          this.$data.map.on('click', this.handleMapClick);

          //add onChange function to radius form element
          $('#radius').change(this.handleRadiusChange);
        },
        handleMapClick: function(e) {
          /* fix for leaflet
           * leaflet allows the map to scroll left/right indefinitely, resulting in longitiude values that are outside the -180 to 180 range
           * if a user clicks in one of these areas, the longitude is adjusted accordingly
           */
          while(e.latlng.lng > 180) {
            e.latlng.lng -= 360;
          }
          while(e.latlng.lng < -180) {
            e.latlng.lng += 360;
          }

          this.$data.map.setView([e.latlng.lat,e.latlng.lng]);  //center the map on the location clicked
          this.$data.mapMarker.setLatLng(e.latlng).addTo(this.$data.map);  //place a marker
          this.$data.mapCircle.setLatLng(e.latlng).setRadius(this.formData.radius).addTo(this.$data.map); //place the area of coverage circle
          this.formData.latitude = e.latlng.lat;  //set hidden latitude form field
          this.formData.longitude = e.latlng.lng;  //set hidden longitude form field
        },
        handleRadiusChange: function() {
          this.$data.mapCircle.setRadius(this.formData.radius)
        },
        setMapLatLng: function(position) {
          if (typeof position.coords.latitude !== 'undefined' && typeof position.coords.longitude !== 'undefined') {
            this.$data.map.setView([position.coords.latitude, position.coords.longitude], this.$data.defaultMapZoom);
          }
        },
        showServerErrors: function() {
          //if validation errors were returned from the server
          if(this.errors.length) {
            var form = $('form');
            var errorList = $('ul.errorMessages', form);
            errorList.empty();
            var errorHTML = '';
            this.errors.forEach((error, i) => {
                  errorHTML += '<li>' + error + '</li>';
                }
            );
            errorList.show().append(errorHTML);
          }
        },
        createAllErrors: function() {
          var form = $('form');
          $('input[type=submit], button[type=submit]', form).on('click', this.showAllErrorMessages);
          $('input[type=text]', form).on('keypress', function(event) {
            if (this.id == 'map_address_search') {
              return;
            }
            //keyCode 13 = Enter
            if (event.keyCode == 13) {
              this.showAllErrorMessages();
            }
          });
        },
        showAllErrorMessages: function() {
            var form = $('form');
            var errorList = $('ul.errorMessages', form);
            errorList.empty();

            //Find all invalid fields within the form.
            var firstErrorTabSelected = false; //the tab containing the field with the first error should be automatically selected
            form.find(':invalid').each(function(index, node) {
              //Find the field's corresponding label
              var label = $('label[for=' + node.id + ']');

              //check if the tab with the first error has been selected yet
              if(!firstErrorTabSelected) {
                var tabId = $(node).parent().parent()[0].id + '-tab';  //get the tab id
                $('#' + tabId).click();  //call it's click method
                firstErrorTabSelected = true;
              }

              //Opera incorrectly does not fill the validationMessage property.
              var message = node.validationMessage || 'Invalid value.';
              errorList
                .show()
                .append('<li><span>' + label.html() + '</span>&nbsp;&nbsp;&nbsp;' + message + '</li>');
            });
          },
      } //close methods
    } //close export
</script>
<style>
  .errorMessages {
      display: none;
      list-style-type: none;
  }
  .errorMessages span {
      font-weight: bold;
  }
  .prev-button {
    margin-right: 10px;
  }
  .btn {
    margin-top: 25px;
  }
  .map-address-suggestions {
    max-height: 220px;
    overflow-y: auto;
  }
  .map-address-search-row {
    display: flex;
    align-items: stretch;
    gap: 12px;
  }
  .map-address-search-row .form-control {
    flex: 1 1 auto;
  }
  .map-find-address-button {
    white-space: nowrap;
    min-width: 160px;
  }
  .map-location-help-banner {
    margin-top: 16px;
  }
</style>
